import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getUrlParamsFetch,
  handleAxiosError,
  notFoundIdHandler,
  toApiError,
} from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { CreatePlanificadorForm, PermanentBlockTimeForm, Planificador, PlanificadoresPagingRes, TimeMapPlanificador, UnblockTimeForm } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { toast } from 'react-toastify';

const { get, post, put } = erpApi();

///* tanSctack query
export enum PlanificadoresKeyActionsEnum {
  PLANIFICADORES = 'planificadores',
  PLANIFICADOR = 'planificador',
}

export const useFetchPlanificadores = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetPlanificadoresParams>) => {
  return useQuery({
    queryKey: [
      PlanificadoresKeyActionsEnum.PLANIFICADORES,
      ...Object.values(params || {}),
    ],
    queryFn: async () => {
      return getPlanificadores(params);
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

export const useGetPlanificador = (id: string) => {
  return useQuery({
    queryKey: [PlanificadoresKeyActionsEnum.PLANIFICADOR, id],
    queryFn: async () => {
      return getPlanificador(id);
    },
    retry: false,
  });
};

export const useCreatePlanificador = ({
  navigate,
  enableRedirect = true,
  returnUrl,

  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADORES],

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  //const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPlanificador,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keysToInvalidate,
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Planificador creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {

      const errorApi = toApiError(error)
      toast.error(`${errorApi.message} [${errorApi.code}]`)
    
    },
    onSettled: () => {
      //setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePlanificador = ({
  navigate,
  enableRedirect = true,
  returnUrl,

  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADORES],

  enableSuccessAlert = true,
  customSuccessMessage,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePlanificador,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Planificador actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetPlanificadoresParams = {
  page?: number;
  page_size?: number;

  // search
  flota?: string; // codigo
  id_planificador?: string;
  fecha_inicio?: string;
  fecha_fin?: string; // date 'YYYY-MM-DD'
  order_up?: boolean;
  fecha_inicio_exact?: string; // date 'YYYY-MM-DD' // exact date
  fecha_final_exact?: string; // date 'YYYY-MM-DD' // exact date
  flota_codigo?: string;
  planificador_id?: number;
  flota_codigo_exact?: string;
};

export type CreatePlanificadorParams = CreatePlanificadorForm;
export type UpdatePlanificadorParams = {
  id: string;
  data: Omit<Planificador, 'id_planificador'>;
};

export const getPlanificadores = async (params?: GetPlanificadoresParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<PlanificadoresPagingRes>(
    `${urlBackend}api/v1/planificador/?${queryParams}`,
    true
  );
};

export const getPlanificador = async (id: string) => {
  const res = await get<Planificador>(
    `${urlBackend}api/v1/planificador/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createPlanificador = async (data: CreatePlanificadorParams) => {

  return post<Planificador>(`${urlBackend}api/v1/planificador/`, data, true);
};

export const updatePlanificador = async ({
  id,
  data,
}: UpdatePlanificadorParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Planificador>(
    `${urlBackend}api/v1/planificador/${id}/`,
    data,
    true
  );
};

/////* block / unblock time
export const usePermanentBlockTime = ({
  navigate,
  returnUrl,
  redirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: permanentBlockTime,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });
    },
    onSettled: () => {
      redirect && returnUrl && navigate && navigate(returnUrl);
    },
  });
};

export const useUnblockTime = ({
  navigate,
  returnUrl,
  redirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unblockTime,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });
    },
    onSettled: () => {
      redirect && returnUrl && navigate && navigate(returnUrl);
    },
  });
};

type PermanentBlockTimeParams = {
  id: string;
  data: PermanentBlockTimeForm;
};
type UnblockTimeParams = {
  id: string;
  data: UnblockTimeForm;
};

export const permanentBlockTime = async (params: PermanentBlockTimeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put(
    `${urlBackend}api/v1/planificador-permablock/${params.id}/`,
    params.data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const unblockTime = async (params: UnblockTimeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put(
    `${urlBackend}api/v1/planificadores-unblock/${params.id}/`,
    params.data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

// // // * New planificador Actions ========================================
///* block hours -------------
export type BloquearHorasParams = {
  fecha: string;
  flota: string; // code fk
  motivo?: string;

  // multi slots/events selected ----
  horas: number[];

  id_preventa?: string; // fk
  user_id?: number;
  tiempo?: string;
  forzar?: boolean;
};
export const blockManyHours = async (params: BloquearHorasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<TimeMapPlanificador[]>(`${urlBackend}api/v1/planificador-bloquear-horas/`, params, true);
};
export const useBlockManyHours = ({
  navigate,
  returnUrl,
  enableRedirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
  enableAlert = true,
  customSuccessMessage,
  customErrorCallback,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: blockManyHours,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Horas bloqueadas correctamente',
          AlertSeverityEnum.success
        );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      customErrorCallback && customErrorCallback(error);
      handleAxiosError(error);
    },
  });
};

/////* unblock hours -------------
export type DesbloquearHorasParams = {
  fecha: string;
  flota: string; // code fk
  motivo?: string;

  // multi slots/events selected ----
  horas: number[];

  id_preventa?: number; // fk
  user_id?: number;
  tiempo?: string;
};
export const unblockManyHours = async (params: DesbloquearHorasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(false);

  return post<TimeMapPlanificador[]>(
    `${urlBackend}api/v1/planificador-desbloquear-horas/`,
    params,
    true
  );
};
export const useUnblockManyHours = ({
  navigate,
  returnUrl,
  enableRedirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
  enableAlert = true,
  customSuccessMessage,
  customErrorCallback,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: unblockManyHours,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Horas desbloqueadas correctamente',
          AlertSeverityEnum.success
        );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      customErrorCallback && customErrorCallback(error);

      handleAxiosError(error);
    },
  });
};

///* block all hours days range -------------
export type BloquearHorasDayRangeParams = {
  fecha_inicio: string;
  fecha_fin: string;
  flota: string; // code fk
  motivo?: string;

  user_id?: number;
};
export const blockHoursDayRange = async (
  params: BloquearHorasDayRangeParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Planificador[]>(
    `${urlBackend}api/v1/planificador-bloquear-dias-rango/`,
    params,
    true
  );
};
export const useBlockHoursDayRange = ({
  navigate,
  returnUrl,
  enableRedirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
  enableAlert = true,
  customSuccessMessage,
  customErrorCallback,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: blockHoursDayRange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Horas bloqueadas correctamente',
          AlertSeverityEnum.success
        );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      customErrorCallback && customErrorCallback(error);

      handleAxiosError(error);
    },
  });
};

///* unblock all hours day range -------------
export type DesbloquearHorasDayRangeParams = {
  fecha_inicio: string;
  fecha_fin: string;
  flota: string; // code fk
  motivo?: string;

  user_id?: number;
};
export const unblockHoursDayRange = async (
  params: DesbloquearHorasDayRangeParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Planificador[]>(
    `${urlBackend}api/v1/planificador-desbloquear-dias-rango/`,
    params,
    true
  );
};
export const useUnblockHoursDayRange = ({
  navigate,
  returnUrl,
  enableRedirect = true,
  keysToInvalidate = [PlanificadoresKeyActionsEnum.PLANIFICADOR],
  enableAlert = true,
  customSuccessMessage,
  customErrorCallback,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: unblockHoursDayRange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keysToInvalidate });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Horas desbloqueadas correctamente',
          AlertSeverityEnum.success
        );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      customErrorCallback && customErrorCallback(error);

      handleAxiosError(error);
    },
  });
};

///* ======================================
///* get timeMaps -------------
export const getFlotaPlanificadorTimeMaps = async ({
  flotaCode,
}: // date,
  {
    flotaCode: string;
    date?: string;
  }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post(
    `${urlBackend}api/v1/obtener-time-maps/`,
    {
      flota: flotaCode,
      limite: 6 ///* TODO: Valor debe ser dinamico
    },
    true
  );
};
export const useGetFlotaPlanificadorTimeMapsMutation = ({
  navigate,
  returnUrl,
  returnErrorUrl,
  enableRedirect = true,

  // customSuccessMessage,
  // enableAlert = true,
  customSuccessCallback,

  enableErrorRedirect = false,
  customErrorCallback,
  customMessageErrorAlert,
  customMessageErrorSeverityAlert,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  // const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: getFlotaPlanificadorTimeMaps,
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [PlanificadoresKeyActionsEnum.PLANIFICADORES],
      });

      // enableAlert &&
      //   setAlert(
      //     true,
      //     customSuccessMessage ?? 'Time Map obtenido correctamente',
      //     AlertSeverityEnum.success
      //   );

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      customErrorCallback && customErrorCallback(error);

      handleAxiosError(
        error,
        customMessageErrorAlert,
        customMessageErrorSeverityAlert
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const postPlanificadorTimeMaps = async ({
  flota,
  limite = 1,
  fecha }: {
    flota: string;
    fecha?: string;
    limite?: number
  }) => {

  return post<TimeMapPlanificador[]>(
    `${urlBackend}api/v1/obtener-time-maps/`,
    {
      flota,
      limite,
      fecha
    },
    true
  );
};